import { Button, Form, Input, Radio, RadioChangeEvent, DatePicker, Select, Typography } from 'antd';
import React, { useState } from 'react';
import { NameOf } from 'src/utils';
import moment, { Moment } from 'moment'; 
import ApiController from '../api/ApiController';
import { useStateAsync } from 'src/hooks';

const RequestTypeValues = ['Summary', 'Transactions', 'Groups'] as const;
export type RequestTypeType = (typeof RequestTypeValues)[number]; // TypeType, hilarious

export interface APILookupFormProps {
    isLoading?: boolean;
    maxDayRange: number; // Indicates the number of days that are selectable
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;    
    onSubmit?: (values: APILookupFormFormData) => void;
    viewChanged: (e: RadioChangeEvent) => void;
}
const { RangePicker } = DatePicker;

export interface APILookupFormFormData {
    license: string;
    requestType: RequestTypeType;
    dateRange: [Moment | null, Moment | null];
    type: string;
}
interface selectItem {
    value: string;
    label: string;
    key: string;
}
type RangeValue = [Moment | null, Moment | null] | null;

const APILookupForm: React.FC<APILookupFormProps> = ({ isLoading, maxDayRange, setIsLoading, onSubmit, viewChanged }) => {
    const [selectedDateRange, setSelectedDateRange , getSelectedDateRangeAsync] = useStateAsync<RangeValue>(null);
    const [tempSelectedDateRange, setTempSelectedDateRange, getTempSelectedDateRangeAsync] = useStateAsync<RangeValue>(null);
    const [currentDirection, setCurrentDirection] = useState<'start' | 'end' | null>(null);
    const [selectTypes, setSelectTypes] = useState<selectItem[]>([]); 
    const [selectDisabled, setSelectDisabled] = useState<boolean | undefined>(true);
    const [rangePickerDisabled, setRangePickerDisabled] = useState<boolean | undefined>(true);
    const [dateRequired, setDateRequired] = useState<boolean>(false);
    const [form] = Form.useForm();
    const layout = {
        labelCol: { span: 6 },
        wrapperCol: { span: 18 },
    };
    const disabledDate = (current: Moment) => {

        // No dates have been selected, so disable none
        if (!currentDirection || !selectedDateRange) {
            return false;
        }

        // Start date has been selected, so disable every date except 31 days ahead of start date
        if (currentDirection === 'start') {
            return current.diff(selectedDateRange[0], 'days') > maxDayRange;
        }

        // End date has been selected, so disable every date except 31 days before the end date
        else {
            return selectedDateRange[1]!.diff(current, 'days') > maxDayRange;
        }
    };
    const onRangePickerOpenChange = async (open: boolean) => {
        if (open) {
            setTempSelectedDateRange(selectedDateRange);
            return;
        } else {
            setCurrentDirection(null);
            const selectedRange = await getSelectedDateRangeAsync();
            const previousRange = await getTempSelectedDateRangeAsync();
            const startDate = selectedRange?.[0];
            const endDate = selectedRange?.[1];
            const rangeIsIncomplete = !startDate || !endDate;
            const rangeIsTooLarge = startDate && endDate && Math.abs(endDate.diff(startDate, 'days')) > maxDayRange;
             if (rangeIsIncomplete || rangeIsTooLarge) {
                // Restore the last safe range in component state.
                setSelectedDateRange(previousRange);

                // Restore the value displayed and stored by Ant Design Form.
                form.setFieldValue(NameOf<APILookupFormFormData>('dateRange'), previousRange);
                return;
            }
        }
    };
    const radioChanged = async (e: RadioChangeEvent): Promise<void> => {
        setIsLoading(true);         
        if (dateRequired)
            setDateRequired(false);
        await form.validateFields(['dateRange']);
        form.setFieldValue(NameOf<APILookupFormFormData>('type'), undefined);  
        setSelectedDateRange(null); 
        form.setFieldValue(NameOf<APILookupFormFormData>('dateRange'), undefined);  
        let resultStrings: string[] = [];
        try {
            const results = await ApiController.getTypesForLocationData(e.target.value);
            resultStrings = results.data.sort((a, b) => {
                if (a.toUpperCase() === 'ALL')
                    return -1;
                else if (b.toUpperCase() === 'ALL')
                    return 1;
                else
                    return a.localeCompare(b);
            });
            const list = resultStrings.map((item) => {
                return { value: item, label: item, key: item };
            });
            setSelectTypes(list);
            if (list.length)
                form.setFieldValue(NameOf<APILookupFormFormData>('type'), list[0].value);
        }
        catch (e) { console.log('error while loading types. is password valid'); }
        viewChanged(e);
        setIsLoading(false);
        switch (e.target.value) {
            case 'Transactions': {                
                setRangePickerDisabled(false);
                setDateRequired(true);
                setSelectDisabled(false);
                const now = moment();
                const earlier = moment().subtract(maxDayRange, 'day');
                setSelectedDateRange([earlier, now]);
                form.setFieldValue(NameOf<APILookupFormFormData>('dateRange'), [earlier, now]);
                break;
            }
            case 'Groups': {                
                setRangePickerDisabled(true);
                setSelectDisabled(false);
                break;
            }
            default:
                setRangePickerDisabled(true);
                setSelectDisabled(true);
                break;
        }
    };
    return (
        <>
            <Form
                labelWrap={true}
                {...layout}
                colon={false}
                disabled={isLoading}
                initialValues={{ requestType: 'Summary', dateRange: selectedDateRange }}
                layout='horizontal'                
                form={form}
                onFinish={onSubmit}
            >
                {/* License # */}
                <Form.Item
                    label={<Typography.Text strong style={{whiteSpace: 'nowrap'}}>License #</Typography.Text>}
                    name={NameOf<APILookupFormFormData>('license')}
                    rules={[
                        { required: true, message: 'License number is required', min: 1 }
                    ]}
                >
                    <Input />
                </Form.Item>
                {/* Radio Buttons */}
                <Form.Item
                    name={NameOf<APILookupFormFormData>('requestType')}
                    label={<Typography.Text strong>View</Typography.Text>}
                >
                    <Radio.Group onChange={radioChanged} options={RequestTypeValues.map(x => ({ label: x, value: x }))} />
                </Form.Item>

                {/* Date range picker*/}
                <Form.Item
                    required={dateRequired}
                    label={<Typography.Text strong>Date Range</Typography.Text>}
                    name={NameOf<APILookupFormFormData>('dateRange')}
                    rules={[{
                        required: dateRequired, message: 'Date is required'
                    }]}
                >
                    
                    <RangePicker
                        format={'L'}
                        value={selectedDateRange}
                        disabled={rangePickerDisabled} // Disables the Range Picker
                        disabledDate={disabledDate} // Disables dates accordingly
                        onCalendarChange={(values, _, info) => {
                            setSelectedDateRange(values);
                            setCurrentDirection(info.range);
                        }}
                        onChange={(values) => 
                            setSelectedDateRange(values)
                        }
                        onOpenChange={onRangePickerOpenChange}
                    />
                </Form.Item>
                <Form.Item label={<Typography.Text strong>Type</Typography.Text>} name={NameOf<APILookupFormFormData>('type') }>
                    <Select options={selectTypes} disabled={selectDisabled} ></Select>
                </Form.Item>
                <Form.Item style={{ textAlign: 'right' }} wrapperCol={{span: 24} } >
                    <Button type='primary' htmlType="submit" loading={isLoading}>Go</Button>
                </Form.Item>
            </Form>
        </>
    );
};

export default APILookupForm;
